import React, { createContext, useContext, useState, useEffect } from 'react'
import { supabase, createProfile, updateProfile, createTransaction, getTransactions, deleteTransaction, createSubscription, getSubscriptions, deleteSubscription, createGoal, getGoals, deleteGoal, updateGoal } from '../lib/supabase'
import { useAuth } from './AuthContext'

const SupabaseContext = createContext()

export function useSupabase() {
  return useContext(SupabaseContext)
}

export function SupabaseProvider({ children }) {
  const { user } = useAuth()
  const [profile, setProfile] = useState(null)
  const [transactions, setTransactions] = useState([])
  const [subscriptions, setSubscriptions] = useState([])
  const [goals, setGoals] = useState([])
  const [loading, setLoading] = useState(false)

  // Fetch user data when user changes
  useEffect(() => {
    if (user) {
      fetchUserData()
    } else {
      // Clear data when user logs out
      setProfile(null)
      setTransactions([])
      setSubscriptions([])
      setGoals([])
    }
  }, [user])

  const fetchUserData = async () => {
    if (!user) return
    
    console.log('Fetching user data for:', user.id)
    setLoading(true)
    try {
      // Fetch profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle()
      
      console.log('Profile data fetched:', profileData)
      console.log('Profile error:', profileError)
      
      // If no profile exists, create one
      if (!profileData) {
        console.log('No profile found, creating default profile')
        try {
          const newProfile = await createProfile({
            id: user.id,
            email: user.email,
            name: '',
            monthly_income: 0,
            salary_date: new Date().toISOString().split('T')[0]
          })
          console.log('Created new profile:', newProfile)
          setProfile(newProfile)
        } catch (createError) {
          console.error('Error creating profile:', createError)
          // Set default profile if creation fails
          setProfile({ 
            id: user.id, 
            email: user.email, 
            name: '', 
            monthly_income: 0, 
            salary_date: new Date().toISOString().split('T')[0] 
          })
        }
      } else {
        console.log('Profile found, setting profile data')
        setProfile(profileData)
      }

      // Fetch transactions
      const transactionsData = await getTransactions(user.id)
      setTransactions(transactionsData)

      // Fetch subscriptions
      const subscriptionsData = await getSubscriptions(user.id)
      setSubscriptions(subscriptionsData)

      // Fetch goals
      const goalsData = await getGoals(user.id)
      setGoals(goalsData)
    } catch (error) {
      console.error('Error fetching user data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Profile functions
  const updateUserProfile = async (profileData) => {
    if (!user) return { success: false, error: 'Not authenticated' }
    
    try {
      console.log('Updating profile with data:', profileData)
      const updatedProfile = await updateProfile(user.id, profileData)
      console.log('Profile updated in database:', updatedProfile)
      
      // Update profile state by merging with existing profile
      setProfile(prev => {
        console.log('Previous profile:', prev)
        const newProfile = {
          ...prev,
          ...updatedProfile,
          ...profileData
        }
        console.log('New profile state:', newProfile)
        return newProfile
      })
      
      return { success: true, data: updatedProfile }
    } catch (error) {
      console.error('Profile update error:', error)
      return { success: false, error: error.message }
    }
  }

  // Transaction functions
  const addTransaction = async (transactionData) => {
    if (!user) return { success: false, error: 'Not authenticated' }
    
    try {
      console.log('Adding transaction:', transactionData)
      console.log('User ID:', user.id)
      
      const newTransaction = await createTransaction({
        ...transactionData,
        user_id: user.id
      })
      
      console.log('Transaction created:', newTransaction)
      setTransactions(prev => [newTransaction, ...prev])
      return { success: true, data: newTransaction }
    } catch (error) {
      console.error('Error adding transaction:', error)
      return { success: false, error: error.message }
    }
  }

  const removeTransaction = async (id) => {
    if (!user) return { success: false, error: 'Not authenticated' }
    
    try {
      await deleteTransaction(id)
      setTransactions(prev => prev.filter(t => t.id !== id))
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  // Subscription functions
  const addSubscription = async (subscriptionData) => {
    if (!user) return { success: false, error: 'Not authenticated' }
    
    try {
      const newSubscription = await createSubscription({
        ...subscriptionData,
        user_id: user.id
      })
      setSubscriptions(prev => [newSubscription, ...prev])
      return { success: true, data: newSubscription }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const removeSubscription = async (id) => {
    if (!user) return { success: false, error: 'Not authenticated' }
    
    try {
      await deleteSubscription(id)
      setSubscriptions(prev => prev.filter(s => s.id !== id))
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  // Goal functions
  const addGoal = async (goalData) => {
    if (!user) return { success: false, error: 'Not authenticated' }
    
    try {
      const newGoal = await createGoal({
        ...goalData,
        user_id: user.id
      })
      setGoals(prev => [newGoal, ...prev])
      return { success: true, data: newGoal }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const removeGoal = async (id) => {
    if (!user) return { success: false, error: 'Not authenticated' }
    
    try {
      await deleteGoal(id)
      setGoals(prev => prev.filter(g => g.id !== id))
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const updateGoalProgress = async (id, amount) => {
    if (!user) return { success: false, error: 'Not authenticated' }
    
    try {
      const goal = goals.find(g => g.id === id)
      if (!goal) return { success: false, error: 'Goal not found' }
      
      const updatedGoal = await updateGoal(id, {
        current_amount: goal.current_amount + amount
      })
      
      setGoals(prev => prev.map(g => g.id === id ? updatedGoal : g))
      return { success: true, data: updatedGoal }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  const markGoalCompleted = async (id) => {
    if (!user) return { success: false, error: 'Not authenticated' }
    
    try {
      const updatedGoal = await updateGoal(id, { completed: true })
      setGoals(prev => prev.map(g => g.id === id ? updatedGoal : g))
      return { success: true, data: updatedGoal }
    } catch (error) {
      return { success: false, error: error.message }
    }
  }

  // Computed values
  const totalIncome = transactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0) + (profile?.monthly_income || 0)

  const totalExpenses = transactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0)

  const totalSubscriptions = subscriptions
    .reduce((sum, s) => sum + parseFloat(s.amount || 0), 0)

  const balance = totalIncome - totalExpenses - totalSubscriptions

  const value = {
    // Data
    profile,
    transactions,
    subscriptions,
    goals,
    loading,
    
    // Computed values
    totalIncome,
    totalExpenses,
    totalSubscriptions,
    balance,
    
    // Functions
    updateUserProfile,
    addTransaction,
    removeTransaction,
    addSubscription,
    removeSubscription,
    addGoal,
    removeGoal,
    updateGoalProgress,
    markGoalCompleted,
    fetchUserData
  }

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  )
}
